import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus, Zap, Clock, Play, Globe, MessageSquare, Mail,
  MoreHorizontal, Trash2, Activity, CheckCircle2, XCircle,
  Loader2, ToggleLeft, ToggleRight,
} from 'lucide-react';
import { api } from '../api.js';
import { fmtDate, fmtDuration, statusBadge } from '../constants.js';

const TRIGGER_ICONS = {
  webhook: Globe,
  cron:    Clock,
  manual:  Play,
};

function WorkflowCard({ workflow, onDelete, onToggle }) {
  const nav = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const TIcon = TRIGGER_ICONS[workflow.trigger_type] ?? Globe;
  const last  = workflow.last_run;

  async function handleDelete(e) {
    e.stopPropagation();
    if (!confirm(`Delete "${workflow.name}"? This cannot be undone.`)) return;
    setDeleting(true);
    await onDelete(workflow.id);
  }

  async function handleToggle(e) {
    e.stopPropagation();
    setMenuOpen(false);
    await onToggle(workflow.id);
  }

  return (
    <div
      onClick={() => nav(`/workflows/${workflow.id}`)}
      style={{
        background:  'var(--surface)',
        border:      '1px solid var(--border)',
        borderRadius: 12,
        cursor:      'pointer',
        transition:  'border-color .15s, box-shadow .15s',
        position:    'relative',
      }}
      className="p-5 hover:border-[#2A3A55] hover:shadow-xl group"
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 min-w-0">
          {/* Active dot */}
          <span
            style={{
              width: 8, height: 8, borderRadius: '50%', flex: '0 0 auto',
              background: workflow.is_active ? 'var(--green)' : 'var(--text-muted)',
              boxShadow:  workflow.is_active ? '0 0 6px var(--green)' : 'none',
            }}
          />
          <h3
            style={{
              fontFamily: 'IBM Plex Mono',
              fontSize: '.9rem',
              fontWeight: 600,
              color: 'var(--text)',
              margin: 0,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {workflow.name}
          </h3>
        </div>

        {/* Menu button */}
        <div className="relative">
          <button
            onClick={(e) => { e.stopPropagation(); setMenuOpen(p => !p); }}
            style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
            className="opacity-0 group-hover:opacity-100 transition-opacity rounded"
          >
            <MoreHorizontal size={16} />
          </button>

          {menuOpen && (
            <div
              style={{
                position: 'absolute', right: 0, top: '100%', marginTop: 4,
                background: 'var(--surface-2)', border: '1px solid var(--border)',
                borderRadius: 8, zIndex: 20, minWidth: 160,
                boxShadow: '0 8px 32px rgba(0,0,0,.5)',
              }}
              onClick={e => e.stopPropagation()}
            >
              <button
                onClick={handleToggle}
                style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '9px 14px', background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: '.85rem' }}
                className="hover:bg-[#252D3D] transition-colors"
              >
                {workflow.is_active
                  ? <><ToggleLeft size={15}/> Deactivate</>
                  : <><ToggleRight size={15}/> Activate</>
                }
              </button>
              <Link
                to={`/workflows/${workflow.id}/runs`}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px', color: 'var(--text-dim)', fontSize: '.85rem', textDecoration: 'none' }}
                className="hover:bg-[#252D3D] transition-colors"
                onClick={e => e.stopPropagation()}
              >
                <Activity size={15}/> View Runs
              </Link>
              <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0' }} />
              <button
                onClick={handleDelete}
                disabled={deleting}
                style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '9px 14px', background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: '.85rem' }}
                className="hover:bg-red-950 transition-colors"
              >
                {deleting ? <Loader2 size={15} className="spin"/> : <Trash2 size={15}/>}
                Delete
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Description */}
      {workflow.description && (
        <p style={{ fontSize: '.8rem', color: 'var(--text-muted)', margin: '0 0 12px', lineHeight: 1.5 }}>
          {workflow.description}
        </p>
      )}

      {/* Meta row */}
      <div className="flex items-center gap-3 flex-wrap" style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>
        {/* Trigger */}
        <span className="flex items-center gap-1.5" style={{ color: 'var(--text-dim)' }}>
          <TIcon size={12} />
          {workflow.trigger_type}
        </span>

        {/* Step count */}
        <span>{workflow.step_count} step{workflow.step_count !== 1 ? 's' : ''}</span>

        {/* Last run */}
        {last && (
          <span className={`badge ${statusBadge(last.status)}`} style={{ fontSize: '.65rem' }}>
            {last.status === 'success'
              ? <CheckCircle2 size={9}/>
              : last.status === 'error'
              ? <XCircle size={9}/>
              : <Loader2 size={9} className="spin"/>
            }
            {last.status}
          </span>
        )}
      </div>

      {/* Run stats footer */}
      <div
        style={{ borderTop: '1px solid var(--border)', marginTop: 14, paddingTop: 12, display: 'flex', gap: 20 }}
      >
        {[
          { label: 'Total runs',  value: workflow.run_stats?.total ?? 0 },
          { label: 'Successes',   value: workflow.run_stats?.successes ?? 0 },
          { label: 'Errors',      value: workflow.run_stats?.errors ?? 0 },
        ].map(({ label, value }) => (
          <div key={label}>
            <div style={{ fontSize: '.7rem', color: 'var(--text-muted)' }}>{label}</div>
            <div style={{ fontFamily: 'IBM Plex Mono', fontSize: '.9rem', fontWeight: 600, color: 'var(--text)' }}>
              {value}
            </div>
          </div>
        ))}
        <div className="flex-1" />
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '.7rem', color: 'var(--text-muted)' }}>Updated</div>
          <div style={{ fontSize: '.75rem', color: 'var(--text-dim)' }}>{fmtDate(workflow.updated_at)}</div>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const nav = useNavigate();
  const [workflows, setWorkflows] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);

  async function load() {
    try {
      setLoading(true);
      const data = await api.workflows.list();
      setWorkflows(data.workflows);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleDelete(id) {
    await api.workflows.delete(id);
    setWorkflows(prev => prev.filter(w => w.id !== id));
  }

  async function handleToggle(id) {
    const data = await api.workflows.toggle(id);
    setWorkflows(prev => prev.map(w => w.id === id ? { ...w, is_active: data.is_active } : w));
  }

  const active = workflows.filter(w => w.is_active).length;
  const total  = workflows.length;

  return (
    <div className="h-full overflow-y-auto" style={{ background: 'var(--bg)' }}>
      <div className="max-w-6xl mx-auto px-6 py-8">

        {/* Header */}
        <div className="flex items-end justify-between mb-8">
          <div>
            <h1 style={{ fontFamily: 'IBM Plex Mono', fontSize: '1.5rem', fontWeight: 600, color: 'var(--text)', margin: 0 }}>
              Workflows
            </h1>
            {!loading && (
              <p style={{ color: 'var(--text-muted)', fontSize: '.85rem', marginTop: 4 }}>
                {active} active · {total} total
              </p>
            )}
          </div>

          <button className="btn-primary" onClick={() => nav('/workflows/new')}>
            <Plus size={16} /> New Workflow
          </button>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center gap-2" style={{ color: 'var(--text-muted)' }}>
            <Loader2 size={16} className="spin" /> Loading workflows…
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{ background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.3)', borderRadius: 10, padding: '16px 20px', color: 'var(--red)' }}>
            <strong>Failed to load workflows:</strong> {error}
            <br />
            <button onClick={load} style={{ marginTop: 8, fontSize: '.85rem', color: 'var(--red)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
              Retry
            </button>
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && workflows.length === 0 && (
          <div
            style={{
              border: '2px dashed var(--border)',
              borderRadius: 16,
              padding: '60px 40px',
              textAlign: 'center',
            }}
          >
            <Zap size={40} style={{ color: 'var(--primary)', margin: '0 auto 16px' }} />
            <h2 style={{ fontFamily: 'IBM Plex Mono', fontSize: '1.1rem', color: 'var(--text)', margin: '0 0 8px' }}>
              No workflows yet
            </h2>
            <p style={{ color: 'var(--text-muted)', marginBottom: 24, fontSize: '.9rem' }}>
              Build your first automation — connect webhooks, APIs, Slack, Gmail, and custom code.
            </p>
            <button className="btn-primary" onClick={() => nav('/workflows/new')}>
              <Plus size={16} /> Create your first workflow
            </button>
          </div>
        )}

        {/* Grid */}
        {!loading && !error && workflows.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {workflows.map(w => (
              <WorkflowCard
                key={w.id}
                workflow={w}
                onDelete={handleDelete}
                onToggle={handleToggle}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
